import React, { useState, useEffect } from 'react';
import { User, Lock, Mail, Phone, UserPlus, MapPin, Map, Home, RefreshCw, Shield } from 'lucide-react';
import AuthFormContainer from './AuthFormContainer';

// Generate simple CAPTCHA
const generateCaptcha = () => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789';
  return Array.from({ length: 6 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
};

export default function RegistrationPage({ onRegister, setPage }) {
  const [formData, setFormData] = useState({
    name: '', mobile: '', email: '', password: '', gst: '',
    address: '', city: '', state: '', pincode: ''
  });
  const [error, setError] = useState('');
  const [captcha, setCaptcha] = useState('');
  const [captchaInput, setCaptchaInput] = useState('');
  const [loading, setLoading] = useState(false);
  const API_BASE_URL = window._config?.apiBaseUrl || '';
  const DATABASE_KEY = window._config?.databaseKey || '';

  useEffect(() => {
    setCaptcha(generateCaptcha());
  }, []);

  const regenerateCaptcha = () => setCaptcha(generateCaptcha());

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const validateEmail = (email) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { name, mobile, email, password, address, city, state, pincode, gst } = formData;

    // Required field check
    if (!name || !mobile || !email || !password || !address || !city || !state || !pincode) {
      setError('Please fill in all required fields.');
      return;
    }

    if (!/^[0-9]{10}$/.test(mobile)) {
      setError('Mobile number must be exactly 10 digits.');
      return;
    }

    if (!/^[0-9]{6}$/.test(pincode)) {
      setError('Pincode must be exactly 6 digits.');
      return;
    }

    if (!validateEmail(email)) {
      setError('Invalid email format.');
      return;
    }

    // Password validation: min 5 chars, at least 1 letter and 1 number
// if (!/^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d]{5,}$/.test(password)) {
//   setError('Password must be at least 5 characters long and include both letters and numbers.');
//   return;
// }

if (!/^(?=.*[A-Za-z])(?=.*\d).{5,}$/.test(password)) {
  setError('Password must be at least 5 characters long and include both letters and numbers.');
  return;
 }


    if (captchaInput.trim().toLowerCase() !== captcha.toLowerCase()) {
      setError('Captcha did not match. Try again.');
      regenerateCaptcha();
      return;
    }

    const payload = {
      Vid: 0,
      VCode: mobile,
      Vname: name,
      Address1: address,
      City: city,
      State: state,
      Pincode: pincode,
      GST: gst,
      VType: 'Customer',
      Phone: mobile,
      Mail: email,
      Mobile: mobile,
      Active: 1,
      ComId: password,
      Sp_SqlAction: 'Insert',
      StateCode:0
    };

   try {
  setLoading(true);
    const response = await fetch(`${API_BASE_URL}/DMLNewVendorOrimShort?databaseKey=${DATABASE_KEY}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  });
 if (!response.ok) throw new Error('Failed to register.');

  const data = await response.json();

  if (data?.DMLVendorOrim?.Table?.[0]?.Status === 'Error') {
    const message = data.DMLVendorOrim.Table[0].Message;
  if (message === 'Duplicate Number') {
  setError('Number already exists.');
  return;
 } else if (message === 'Duplicate Email') {
  setError('Email already exists.');
  return;
 }
  }
 onRegister(formData); // Pass email and OTP-related info
 setPage('verify-email');

 } catch (err) {
  console.error(err);
  setError('Registration failed. Try again later.');
 } finally {
  setLoading(false);
 }
  };

return (
  <div className="relative flex items-center justify-center min-h-screen px-4 py-10 overflow-hidden bg-gradient-to-br from-pink-100 via-rose-50 to-teal-100">

    {/* 🌸 Floating Flowers (Same Style as Login) */}
    <div className="absolute text-6xl top-10 left-10 animate-bounce opacity-20">🌸</div>
    <div className="absolute text-5xl bottom-20 right-16 animate-pulse opacity-20">🌷</div>
    <div className="absolute text-4xl top-1/3 right-10 animate-bounce opacity-20">🌺</div>

    <div className="w-full max-w-2xl p-8 transition-all duration-500 border shadow-2xl backdrop-blur-lg bg-white/70 rounded-3xl border-white/40">

      {/* Title */}
      <div className="mb-6 text-center">
        <h1 className="text-3xl font-bold text-teal-700">🌼 Create your Account</h1>

     
      </div>

      <form className="space-y-4" onSubmit={handleSubmit}>

        {error && (
          <p className="p-3 text-sm text-center text-red-600 bg-red-100 rounded-lg">
            {error}
          </p>
        )}

        <Input icon={<User />} name="name" value={formData.name} onChange={handleChange} placeholder="Full Name" />
        <Input icon={<Mail />} name="email" value={formData.email} onChange={handleChange} placeholder="Email Address" />
        <Input icon={<Phone />} name="mobile" value={formData.mobile} onChange={handleChange} placeholder="Mobile Number" maxLength={10} />
        <Input icon={<Lock />} name="password" type="password" value={formData.password} onChange={handleChange} placeholder="Password" />
        <Input icon={<UserPlus />} name="gst" value={formData.gst} onChange={handleChange} placeholder="GST Number "required={false}  />
        <Input icon={<Home />} name="address" value={formData.address} onChange={handleChange} placeholder="Address" />
        <Input icon={<MapPin />} name="city" value={formData.city} onChange={handleChange} placeholder="City" />
        <Input icon={<Map />} name="state" value={formData.state} onChange={handleChange} placeholder="State" />
        <Input icon={<MapPin />} name="pincode" value={formData.pincode} onChange={handleChange} placeholder="Pincode" maxLength={6} />

        {/* CAPTCHA */}
        <div className="p-4 border border-teal-100 bg-teal-50 rounded-xl">
          <label className="block mb-2 text-sm font-medium text-gray-700">
            Security Verification
          </label>

          <div className="flex items-center gap-3">
            <div className="px-4 py-2 text-lg font-bold tracking-widest bg-white border border-gray-200 rounded-lg shadow-inner">
              {captcha}
            </div>

            <button
              type="button"
              onClick={regenerateCaptcha}
              className="text-teal-600 transition hover:text-rose-500"
            >
              <RefreshCw size={18} />
            </button>
          </div>

          <div className="mt-3">
            <Input
              icon={<Shield />}
              name="captchaInput"
              value={captchaInput}
              onChange={(e) => setCaptchaInput(e.target.value)}
              placeholder="Enter Captcha"
            />
          </div>
        </div>

        {/* Register Button – Same Theme as Login */}
        <button
          type="submit"
          disabled={loading}
          className="w-full py-3 font-semibold text-white transition duration-300 transform shadow-lg rounded-xl bg-gradient-to-r from-teal-500 to-rose-400 hover:scale-105 disabled:opacity-60"
        >
          {loading ? "Creating Account..." : "Register 🌸"}
        </button>

        {/* Footer */}
        <div className="mt-4 text-sm text-center">
          Already have an account?{" "}
          <button
            onClick={() => setPage("login")}
            className="font-medium text-rose-500 hover:underline"
          >
            Sign In
          </button>
        </div>

      </form>
    </div>
  </div>
);


}

const Input = ({ icon, name, value, onChange, placeholder, type = 'text', maxLength,required = true }) => (
  <div className="relative">
    <div className="absolute inset-y-0 left-0 flex items-center pl-4 pointer-events-none">
      {React.cloneElement(icon, { className: 'h-5 w-5 text-gray-400' })}
    </div>
    <input
      name={name}
      value={value}
      onChange={onChange}
      type={type}
      maxLength={maxLength}
      required={required} 
      className="block w-full px-3 py-3 pl-12 text-gray-900 placeholder-gray-500 transition-shadow duration-300 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-teal-500 focus:border-teal-500 sm:text-sm"
      placeholder={placeholder}
    />
  </div>
);
